import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { literalTranslations, SupportedLanguage } from './translations';

const STORAGE_KEY = 'app.language';

type I18nContextValue = {
  language: SupportedLanguage;
  setLanguage: (language: SupportedLanguage) => void;
  t: (value: string) => string;
};

const defaultContext: I18nContextValue = {
  language: 'pt-BR',
  setLanguage() {
    return undefined;
  },
  t(value: string) {
    return value;
  },
};

const I18nContext = createContext<I18nContextValue>(defaultContext);
const textOriginalMap = new WeakMap();

function normalizeLanguage(value?: string | null): SupportedLanguage {
  if (value === 'en' || value === 'es' || value === 'pt-BR') {
    return value;
  }

  if (value && value.toLowerCase().indexOf('es') === 0) {
    return 'es';
  }

  if (value && value.toLowerCase().indexOf('en') === 0) {
    return 'en';
  }

  return 'pt-BR';
}

function getWordFallbackMaps() {
  const entries = Object.entries(literalTranslations);
  const en: Record<string, string> = {};
  const es: Record<string, string> = {};

  entries.forEach((entry) => {
    const original = entry[0];
    const langs = entry[1];

    if (!original || original.indexOf(' ') >= 0) {
      return;
    }

    if (langs.en && langs.en !== original) {
      en[original] = langs.en;
    }

    if (langs.es && langs.es !== original) {
      es[original] = langs.es;
    }
  });

  return { en, es };
}

const wordFallbackMaps = getWordFallbackMaps();

function translateByWords(value: string, language: SupportedLanguage): string {
  if (!value || language === 'pt-BR') {
    return value;
  }

  const dict = language === 'en' ? wordFallbackMaps.en : wordFallbackMaps.es;

  return value.replace(/[A-Za-zÀ-ÿ][A-Za-zÀ-ÿ0-9+/-]*/g, (token) => dict[token] || token);
}

export function translateLiteral(value: string, language: SupportedLanguage): string {
  if (!value || language === 'pt-BR') {
    return value;
  }

  const exactEntry = literalTranslations[value];
  if (exactEntry && exactEntry[language]) {
    return exactEntry[language] || value;
  }

  const trimmed = value.trim();
  const trimmedEntry = literalTranslations[trimmed];
  if (trimmedEntry && trimmedEntry[language]) {
    const startIndex = value.indexOf(trimmed);
    const prefix = startIndex >= 0 ? value.slice(0, startIndex) : '';
    const suffix = startIndex >= 0 ? value.slice(startIndex + trimmed.length) : '';
    return `${prefix}${trimmedEntry[language] || trimmed}${suffix}`;
  }

  return translateByWords(value, language);
}

function getElementFromNode(node: any) {
  if (!node) {
    return null;
  }

  if (node.nodeType === 1) {
    return node;
  }

  if (node.parentElement) {
    return node.parentElement;
  }

  return null;
}

function isInsideIgnoredNode(node: Node | null): boolean {
  let current: any = node;

  while (current) {
    const element: any = getElementFromNode(current);

    if (element) {
      const { tagName } = element;

      if (tagName === 'SCRIPT' || tagName === 'STYLE' || tagName === 'NOSCRIPT' || tagName === 'IFRAME' || tagName === 'OPTION') {
        return true;
      }

      if (element.getAttribute && element.getAttribute('data-no-translate') === 'true') {
        return true;
      }

      current = element.parentNode;
    } else {
      current = current.parentNode;
    }
  }

  return false;
}

function getOriginalAttributeValue(element: any, attribute: string): string | null {
  const storageAttr = `data-i18n-original-${attribute}`;
  const stored = element.getAttribute(storageAttr);
  if (stored !== null) {
    return stored;
  }

  const current = element.getAttribute(attribute);
  if (current !== null) {
    element.setAttribute(storageAttr, current);
  }

  return current;
}

function restoreOriginalTexts(root: Node) {
  const walkerRestore = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null, false);
  let textNode: any = walkerRestore.nextNode();

  while (textNode) {
    if (textNode.nodeType === 3) {
      const text: any = textNode;
      const original = textOriginalMap.get(text);

      if (original !== undefined) {
        text.nodeValue = original;
      }
    }

    textNode = walkerRestore.nextNode();
  }
}

function restoreOriginalAttributes(root: any) {
  let container: any = null;

  if (root instanceof Document) {
    container = root.body;
  } else if (root instanceof Element) {
    container = root;
  }

  if (!container) {
    return;
  }

  const elements = container.querySelectorAll('[data-i18n-original-placeholder], [data-i18n-original-title], [data-i18n-original-aria-label], [data-i18n-original-value]');
  elements.forEach((element: any) => {
    ['placeholder', 'title', 'aria-label', 'value'].forEach((attribute) => {
      const original = element.getAttribute(`data-i18n-original-${attribute}`);
      if (original !== null) {
        element.setAttribute(attribute, original);
      }
    });
  });
}

function translateTextNodes(root: Node, language: SupportedLanguage) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null, false);
  let textNode: any = walker.nextNode();

  while (textNode) {
    if (textNode.nodeType === 3) {
      const text: any = textNode;
      const currentValue = text.nodeValue || '';
      const originalFromMap = textOriginalMap.get(text);
      const originalValue = originalFromMap !== undefined ? originalFromMap : currentValue;

      // restante da lógica
    }

    textNode = walker.nextNode();
  }
}

function translateAttributes(root: any, language: SupportedLanguage) {
  let container: any = null;

  if (root instanceof Document) {
    container = root.body;
  } else if (root instanceof Element) {
    container = root;
  }

  if (!container) {
    return;
  }

  const elements = container.querySelectorAll('input[placeholder], textarea[placeholder], [title], [aria-label], input[type="button"], input[type="submit"]');

  elements.forEach((element: any) => {
    ['placeholder', 'title', 'aria-label', 'value'].forEach((attribute) => {
      const original = getOriginalAttributeValue(element, attribute);
      if (original !== null) {
        const translated = translateLiteral(original, language);
        if (translated !== original) {
          element.setAttribute(attribute, translated);
        }
      }
    });
  });
}

function translateDomTree(root: Node, language: SupportedLanguage) {
  if (language === 'pt-BR') {
    restoreOriginalTexts(root);
    restoreOriginalAttributes(root);
    return;
  }

  restoreOriginalTexts(root);
  restoreOriginalAttributes(root);
  translateTextNodes(root, language);
  translateAttributes(root, language);
}

export const I18nProvider = ({ children }: { children: React.ReactNode }) => {
  const [language, setLanguageState] = useState<SupportedLanguage>(() => {
    if (typeof window === 'undefined') {
      return 'pt-BR';
    }

    return normalizeLanguage(window.localStorage.getItem(STORAGE_KEY) || window.navigator.language);
  });

  const observerRef = useRef<MutationObserver | null>(null);

  const setLanguage = useCallback((nextLanguage: SupportedLanguage) => {
    setLanguageState(nextLanguage);

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, nextLanguage);
    }
  }, []);

  const t = useCallback((value: string) => translateLiteral(value, language), [language]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const translate = () => {
      if (document.body) {
        translateDomTree(document.body, language);
      }
    };

    translate();

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new MutationObserver(() => {
      translate();
    });

    if (document.body && observerRef.current) {
      observerRef.current.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [language]);

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t,
    }),
    [language, setLanguage, t]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);
